import type { Forest } from './forest'

export type Selection = {
	readonly roots: ReadonlySet<string>
	readonly isEmpty: boolean
	isChecked(id: string): boolean
	isIndeterminate(id: string): boolean
}

type AnyForest = Forest<unknown>

// Intentional allocation-free early-exit walk (unlike `forest.ancestors()`,
// which allocates an array and unshifts per level) — this runs on every
// `isChecked` call for every visible row, so it's deliberate, not duplication.
const hasSelectedAncestor = (
	forest: AnyForest,
	roots: ReadonlySet<string>,
	id: string
) => {
	let current = forest.parentOf.get(id) ?? null
	while (current !== null) {
		if (roots.has(current)) return true
		current = forest.parentOf.get(current) ?? null
	}

	return false
}

/** Builds a Selection from already-compact roots. O(|roots| × depth). */
export const createSelection = (
	forest: AnyForest,
	roots: Iterable<string>
): Selection => {
	const rootSet = new Set<string>()
	for (const id of roots) if (forest.has(id)) rootSet.add(id)

	// Number of selection roots strictly below each node.
	const partialCount = new Map<string, number>()
	for (const root of rootSet) {
		for (const ancestor of forest.ancestors(root)) {
			partialCount.set(ancestor, (partialCount.get(ancestor) ?? 0) + 1)
		}
	}

	const isChecked = (id: string) =>
		rootSet.has(id) || hasSelectedAncestor(forest, rootSet, id)

	return {
		roots: rootSet,
		isEmpty: rootSet.size === 0,
		isChecked,
		isIndeterminate: (id) => (partialCount.get(id) ?? 0) > 0 && !isChecked(id),
	}
}

/** Replaces sets of fully-selected siblings with their parent, bottom-up. Mutates `set`. */
const coalesce = (forest: AnyForest, set: Set<string>) => {
	const queue: string[] = []
	for (const id of set) {
		const parent = forest.parentOf.get(id) ?? null
		if (parent !== null) queue.push(parent)
	}
	while (queue.length) {
		const parent = queue.pop()!
		if (set.has(parent)) continue
		const children = forest.childrenOf.get(parent) ?? []
		if (children.length && children.every((child) => set.has(child))) {
			for (const child of children) set.delete(child)
			set.add(parent)
			const grandparent = forest.parentOf.get(parent) ?? null
			if (grandparent !== null) queue.push(grandparent)
		}
	}
}

/**
 * Accepts any mix of leaf, mid-level and root ids. Drops unknown ids and ids
 * covered by a selected ancestor, then coalesces upward.
 */
export const normalizeSelection = (
	forest: AnyForest,
	ids: Iterable<string>
): Selection => {
	const set = new Set<string>()
	for (const id of ids) if (forest.has(id)) set.add(id)
	for (const id of [...set]) {
		if (hasSelectedAncestor(forest, set, id)) set.delete(id)
	}
	coalesce(forest, set)

	return createSelection(forest, set)
}

export const toggle = (
	forest: AnyForest,
	selection: Selection,
	id: string
): Selection => {
	if (!forest.has(id)) return selection
	const next = new Set(selection.roots)

	if (selection.isChecked(id)) {
		// Find the covering root (self or ancestor) and split it down to `id`.
		const path = [...forest.ancestors(id), id]
		const coverIndex = path.findIndex((node) => next.has(node))
		next.delete(path[coverIndex])
		for (let i = coverIndex; i < path.length - 1; i++) {
			for (const child of forest.childrenOf.get(path[i]) ?? []) {
				if (child !== path[i + 1]) next.add(child)
			}
		}
	} else {
		for (const root of selection.roots) {
			if (forest.ancestors(root).includes(id)) next.delete(root)
		}
		next.add(id)
		coalesce(forest, next)
	}

	// ponytail: rebuild partial counts from roots (O(|roots| × depth)); patch incrementally if profiling shows toggles are slow
	return createSelection(forest, next)
}

export const selectAll = (forest: AnyForest): Selection =>
	createSelection(forest, forest.roots)

export const clearSelection = (forest: AnyForest): Selection =>
	createSelection(forest, [])
